import React, { useState, useEffect } from 'react';
import { Table, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faFileDownload } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PageTitle from '../../../layouts/PageTitle';
import { NoData } from '../../SharedComponents/NoData';
import { Link, useHistory } from 'react-router-dom';

const BalanceReport = () => {
    const [fiscalYears, setFiscalYears] = useState([]);
    const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [yearPeriodOptions, setYearPeriodOptions] = useState([]); // grouped options per year
    const [showAllYears, setShowAllYears] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ledgerData, setLedgerData] = useState({});
    const [expandedSections, setExpandedSections] = useState({
        'TILLGANGAR': true,
        'EGET KAPITAL OCH SKULDER': true
    });

    const history = useHistory();

    const { companyId } = useParams();

    useEffect(() => {
        getFiscalYears();
    }, [companyId]);

    // Build a single dropdown with year and embedded periods (whole year + months)
    useEffect(() => {
        if (!fiscalYears || fiscalYears.length === 0) return;

        const monthFormatter = new Intl.DateTimeFormat('sv-SE', { month: 'long' });
        const toLocalYMD = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        const sorted = [...fiscalYears].sort((a, b) => b.year - a.year);
        const limited = showAllYears ? sorted : sorted.slice(0, 5);

        const groups = limited
            .map((fy) => {
                const fyStart = new Date(fy.start_date);
                const fyEnd = new Date(fy.end_date);

                const opts = [];
                // Whole year option first
                opts.push({
                    value: {
                        fiscalYear: fy,
                        period: { type: 'year', startDate: fy.start_date, endDate: fy.end_date }
                    },
                    label: 'Hela året'
                });

                // Month options within FY boundaries
                let cursor = new Date(fyStart.getFullYear(), fyStart.getMonth(), 1);
                while (cursor <= fyEnd) {
                    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
                    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
                    const start = monthStart < fyStart ? fyStart : monthStart;
                    const end = monthEnd > fyEnd ? fyEnd : monthEnd;
                    const monthName = monthFormatter.format(monthStart);
                    const label = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${monthStart.getFullYear()}`;

                    opts.push({
                        value: {
                            fiscalYear: fy,
                            period: { type: 'month', startDate: toLocalYMD(start), endDate: toLocalYMD(end) }
                        },
                        label
                    });

                    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
                }

                return { label: String(fy.year), options: opts };
            });

        // Add a "show more" option when not all years are shown
        const hasMore = !showAllYears && sorted.length > limited.length;
        const moreGroup = hasMore ? [{ label: '', options: [{ value: { action: '__more__' }, label: 'Visa fler år…' }] }] : [];

        setYearPeriodOptions([...groups, ...moreGroup]);

        // Default selection: latest FY whole year
        const latestGroup = groups[0];
        if (latestGroup && latestGroup.options && latestGroup.options.length > 0) {
            const def = latestGroup.options[0];
            setSelectedFiscalYear(def.value.fiscalYear);
            setSelectedPeriod({ value: def.value.period, label: def.label });
        }
    }, [fiscalYears, showAllYears]);

    useEffect(() => {
        if (selectedFiscalYear && selectedPeriod) getBalanceReport();
    }, [selectedFiscalYear, selectedPeriod]);

    const getFiscalYears = async () => {
        try {
            const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
            setFiscalYears(result.data.fiscalYears);
            if (result.data.fiscalYears.length > 0) {
                const latestFiscalYear = result.data.fiscalYears.sort((a, b) => b.year - a.year)[0];
                setSelectedFiscalYear(latestFiscalYear);
            }
        } catch (err) {
            setError('Failed to fetch fiscal years');
        } finally {
            setLoading(false);
        }
    };

    const getBalanceReport = async () => {
        if (!selectedFiscalYear) return;

        setLoading(true);

        const queryParams = { fiscalYearId: selectedFiscalYear.fiscal_year_id };
        if (selectedPeriod && selectedPeriod.value?.type === 'month') {
            queryParams.startDate = selectedPeriod.value.startDate;
            queryParams.endDate = selectedPeriod.value.endDate;
        }

        try {
            const response = await axios.get(getApiUrl(`${companyId}/reporting/balance`), {
                params: queryParams,
                withCredentials: true
            });
            setLedgerData(response.data);
        } catch (err) {
            setError('Failed to fetch balance report');
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (sectionPath) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionPath]: !prev[sectionPath]
        }));
    };

    const formatAmount = (amount) => {
        if (!amount) return '0,00';
        return new Intl.NumberFormat('sv-SE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: 'decimal'
        }).format(parseFloat(amount));
    };

    const renderAccountRow = (account, depth) => {
        if (!account) return null;
        return (
            <tr key={account.account_id} className="hover-row">
                <td style={{ paddingLeft: `${(depth + 1) * 1.5}rem` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', overflow: 'hidden' }}>
                        <span style={{ color: '#666', minWidth: '4rem', flexShrink: 0 }}>{account.account_number}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.account_name}</span>
                    </div>
                </td>
                <td style={{ textAlign: 'right' }}>{formatAmount(account.opening_balance)}</td>
                <td style={{ textAlign: 'right' }}>{formatAmount(account.change)}</td>
                <td style={{ textAlign: 'right' }}>{formatAmount(account.closing_balance)}</td>
            </tr>
        );
    };

    const renderSection = (sectionData, sectionName, path = '', depth = 0) => {
        if (!sectionData || typeof sectionData !== 'object') return null;

        const currentPath = path ? `${path}.${sectionName}` : sectionName;
        const hasSubsections = Object.keys(sectionData).some(key =>
            typeof sectionData[key] === 'object' && !['accounts', 'regex'].includes(key)
        );
        const isExpanded = expandedSections[currentPath] !== false;

        return (
            <React.Fragment key={currentPath}>
                <tr className={hasSubsections ? 'cursor-pointer hover-row' : ''}>
                    <td
                        style={{
                            paddingLeft: `${depth * 1.5}rem`,
                            backgroundColor: depth === 0 ? '#f8f9fa' : depth === 1 ? '#ffffff' : 'transparent',
                        }}
                        onClick={() => hasSubsections && toggleSection(currentPath)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {hasSubsections && (
                                <span style={{ color: '#666', width: '20px' }}>
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronDown : faChevronRight}
                                        size="sm"
                                    />
                                </span>
                            )}
                            <strong>{sectionName}</strong>
                        </div>
                    </td>
                    {sectionData.total_opening_balance !== undefined && (
                        <>
                            <td style={{ textAlign: 'right' }}><strong>{formatAmount(sectionData.total_opening_balance)}</strong></td>
                            <td style={{ textAlign: 'right' }}><strong>{formatAmount(sectionData.total_change)}</strong></td>
                            <td style={{ textAlign: 'right' }}><strong>{formatAmount(sectionData.total_closing_balance)}</strong></td>
                        </>
                    )}
                    {!sectionData.total_opening_balance && (
                        <>
                            <td style={{ textAlign: 'right' }}></td>
                            <td style={{ textAlign: 'right' }}></td>
                            <td style={{ textAlign: 'right' }}></td>
                        </>
                    )}
                </tr>

                {isExpanded && (
                    <>
                        {sectionData.accounts?.map(account => renderAccountRow(account, depth))}

                        {Object.entries(sectionData)
                            .filter(([key, value]) =>
                                typeof value === 'object' &&
                                !['accounts', 'regex'].includes(key)
                            )
                            .map(([key, value]) => renderSection(value, key, currentPath, depth + 1))}
                    </>
                )}
            </React.Fragment>
        );
    };

    const handleYearPeriodChange = (opt) => {
        if (!opt || !opt.value) return;
        if (opt.value.action === '__more__') {
            setShowAllYears(true);
            return;
        }
        setSelectedFiscalYear(opt.value.fiscalYear);
        setSelectedPeriod({ value: opt.value.period, label: opt.label });
    };

    const exportToPDF = () => {
        const doc = new jsPDF();

        // Add header information
        doc.setFontSize(14);
        doc.text("Balansräkning", 14, 10);
        doc.setFontSize(11);
        doc.text(`Räkenskapsår: ${selectedFiscalYear?.year || ''}`, 14, 20);
        const periodLabel = selectedPeriod?.label || 'Hela året';
        doc.text(`Period: ${periodLabel}`, 14, 27);

        const tableColumn = ["", "Ingående balans", "Förändring", "Utgående saldo"];

        // Helper function to convert section to rows for PDF
        const getSectionRows = (section, depth = 0) => {
            if (!section || typeof section !== 'object') return [];
            let rows = [];

            Object.keys(section).forEach((key) => {
                const subSection = section[key];
                if (!subSection || typeof subSection !== 'object') return;

                const isLeafNode = subSection.accounts && Array.isArray(subSection.accounts) && subSection.accounts.length > 0;

                rows.push([{ content: key, colSpan: 4, styles: { halign: 'left', fontStyle: 'bold' } }]);

                if (isLeafNode) {
                    subSection.accounts.forEach((account) => {
                        rows.push([
                            `${' '.repeat(depth * 2)}${account.account_number} - ${account.account_name}`,
                            account.opening_balance,
                            account.change,
                            account.closing_balance,
                        ]);
                    });

                    rows.push([
                        { content: `Summa ${key}`, colSpan: 1, styles: { fontStyle: 'bold' } },
                        { content: subSection.total_opening_balance, styles: { fontStyle: 'bold' } },
                        { content: subSection.total_change, styles: { fontStyle: 'bold' } },
                        { content: subSection.total_closing_balance, styles: { fontStyle: 'bold' } }
                    ]);
                } else {
                    rows = rows.concat(getSectionRows(subSection, depth + 1));
                }
            });

            return rows;
        };

        // Add TILLGANGAR section
        doc.autoTable({
            head: [tableColumn],
            body: [[{ content: "Tillgångar", colSpan: 4, styles: { halign: 'left', fontStyle: 'bold' } }]],
            startY: 30,
            styles: { fontSize: 10 },
        });

        doc.autoTable({
            body: getSectionRows(ledgerData.TILLGANGAR),
            startY: doc.lastAutoTable.finalY,
            styles: { fontSize: 10 },
        });

        // Add EGET KAPITAL OCH SKULDER section
        let skulderStartY = doc.lastAutoTable.finalY + 10;

        doc.autoTable({
            head: [tableColumn],
            body: [[{ content: "Eget Kapital och Skulder", colSpan: 4, styles: { halign: 'left', fontStyle: 'bold' } }]],
            startY: skulderStartY,
            styles: { fontSize: 10 },
        });

        doc.autoTable({
            body: getSectionRows(ledgerData['EGET KAPITAL OCH SKULDER']),
            startY: doc.lastAutoTable.finalY,
            styles: { fontSize: 10 },
        });

        const suffix = selectedPeriod?.value?.type === 'month'
            ? `${selectedFiscalYear?.year}_${periodLabel.replace(/\s+/g, '-')}`
            : `${selectedFiscalYear?.year}`;
        doc.save(`BalanceReport_${suffix}.pdf`);
    };

    if (loading) {
        return <p>Loading...</p>;
    }

    if (error) {
        return <p style={{ color: 'red' }}>{error}</p>;
    }

    if (fiscalYears.length === 0) {
        return (
            <>
                <PageTitle motherMenu="Rapporter" activeMenu="Balansrapport" />
                <NoData message="Det finns inga räkenskapsår att visa. Skapa ett nytt räkenskapsår för att börja lägga till verifikationer." />
                <div className="text-center mt-4">
                    <Link
                        to="#"
                        onClick={() => history.push(`/${companyId}/settings`)}
                        className="btn btn-primary"
                    >
                        Gå till Inställningar
                    </Link>
                </div>
            </>
        );
    }

    // Determine period date range for headers
    const periodStart = selectedPeriod?.value?.startDate || selectedFiscalYear?.start_date;
    const periodEnd = selectedPeriod?.value?.endDate || selectedFiscalYear?.end_date;

    return (
        <>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                width: '100%'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span>År/Period:</span>
                    <Select
                        value={(function () {
                            // compute current selection from states
                            const fyId = selectedFiscalYear?.fiscal_year_id;
                            const p = selectedPeriod?.value;
                            if (!fyId || !p) return null;
                            for (const group of yearPeriodOptions) {
                                for (const o of group.options) {
                                    const v = o.value;
                                    if (
                                        v.fiscalYear?.fiscal_year_id === fyId &&
                                        v.period?.type === p.type &&
                                        v.period?.startDate === p.startDate &&
                                        v.period?.endDate === p.endDate
                                    ) {
                                        return o;
                                    }
                                }
                            }
                            return null;
                        })()}
                        onChange={handleYearPeriodChange}
                        options={yearPeriodOptions}
                        styles={{
                            container: (base) => ({ ...base, minWidth: '300px' }),
                            menuList: (base) => ({ ...base, maxHeight: 280 })
                        }}
                    />
                </div>
                <Button variant="primary" onClick={exportToPDF}>
                    <FontAwesomeIcon icon={faFileDownload} style={{ marginRight: '0.5rem' }} />
                    Exportera till PDF
                </Button>
            </div>

            <Card>
                <Card.Header>
                    <h5>Balansrapport</h5>
                </Card.Header>
                <Card.Body style={{ overflowX: 'auto' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: '800px' }}>
                            <Table hover style={{ tableLayout: 'fixed', width: '100%', marginBottom: '0' }}>
                                <colgroup>
                                    <col style={{ width: '40%' }} />
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '20%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={{ width: '40%' }}>Konto</th>
                                        <th style={{ width: '20%', textAlign: 'right' }}>
                                            <div style={{ fontSize: '0.85em' }}>Ingående saldo</div>
                                            <div style={{ fontSize: '0.75em', color: '#666' }}>
                                                {periodStart ? new Date(periodStart).toLocaleDateString('sv-SE') : ''}
                                            </div>
                                        </th>
                                        <th style={{ width: '20%', textAlign: 'right', fontSize: '0.85em' }}>Förändring</th>
                                        <th style={{ width: '20%', textAlign: 'right' }}>
                                            <div style={{ fontSize: '0.85em' }}>Utgående saldo</div>
                                            <div style={{ fontSize: '0.75em', color: '#666' }}>
                                                {periodEnd ? new Date(periodEnd).toLocaleDateString('sv-SE') : ''}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledgerData?.TILLGANGAR && renderSection(ledgerData.TILLGANGAR, 'TILLGANGAR')}
                                </tbody>
                            </Table>

                            <div style={{ marginBottom: '30px' }} />

                            <Table hover style={{ tableLayout: 'fixed', width: '100%' }}>
                                <colgroup>
                                    <col style={{ width: '40%' }} />
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '20%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={{ width: '40%' }}>Konto</th>
                                        <th style={{ width: '20%', textAlign: 'right' }}>
                                            <div style={{ fontSize: '0.85em' }}>Ingående saldo</div>
                                            <div style={{ fontSize: '0.75em', color: '#666' }}>
                                                {periodStart ? new Date(periodStart).toLocaleDateString('sv-SE') : ''}
                                            </div>
                                        </th>
                                        <th style={{ width: '20%', textAlign: 'right', fontSize: '0.85em' }}>Förändring</th>
                                        <th style={{ width: '20%', textAlign: 'right' }}>
                                            <div style={{ fontSize: '0.85em' }}>Utgående saldo</div>
                                            <div style={{ fontSize: '0.75em', color: '#666' }}>
                                                {periodEnd ? new Date(periodEnd).toLocaleDateString('sv-SE') : ''}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledgerData?.['EGET KAPITAL OCH SKULDER'] &&
                                        renderSection(ledgerData['EGET KAPITAL OCH SKULDER'], 'EGET KAPITAL OCH SKULDER')}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <style jsx>{`
        .hover-row:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
        </>
    );
};

export default BalanceReport;
